import type {
  ScholarshipPlayer,
  ScholarshipPlayerWriteRequest,
} from '../../../../types/scholarships';
import { storedClass } from './scholarshipClasses';

/**
 * A row that draws height, weight, school and travel team from a player record. A row whose record
 * has been deleted (`prospectMissing`) holds its own copy again, as a manual row does.
 */
export const isLinkedPlayer = (
  player: Pick<ScholarshipPlayer, 'prospectId' | 'prospectMissing'>,
): boolean => player.prospectId != null && !player.prospectMissing;

/**
 * The PUT body that writes a row back exactly as it is, with `changes` applied on top.
 *
 * The player update is a FULL REPLACE: a field left out is cleared, not kept. So a control that
 * saves one field on its own (the pitching role on the staff table) must send every other field
 * as the row already holds it, or picking a role would quietly wipe the player's money, notes and
 * pitch shapes. Every field the write request carries is listed here, and the round-trip test
 * fails when one is added to the row without being added below.
 *
 * Class goes through storedClass, the same as the edit modal, so a row rewritten in full keeps the
 * class the board is showing it under -- a JC year included, which on an attached row lives on the
 * player record rather than on the row.
 */
export const toPlayerWriteRequest = (
  player: ScholarshipPlayer,
  changes: Partial<ScholarshipPlayerWriteRequest> = {},
): ScholarshipPlayerWriteRequest => {
  const linked = isLinkedPlayer(player);
  return {
    firstName: player.firstName,
    lastName: player.lastName,
    position: player.position,
    secondaryPosition: player.secondaryPosition,
    class: storedClass(player),
    bats: player.bats,
    throws: player.throws,
    cost: player.cost,
    award: player.award,
    scholarshipUnit: player.scholarshipUnit,
    nilAmount: player.nilAmount,
    nilPartner: player.nilPartner,
    revShare: player.revShare,
    academicAmount: player.academicAmount,
    advisor: player.advisor,
    residency: player.residency,
    campus: player.campus,
    comments: player.comments,
    isDsa: player.isDsa,
    evaluationTraitId: player.evaluationTraitId ?? null,
    indicatorIds: player.indicatorIds ?? [],
    playerType: player.playerType ?? null,
    playerStatus: player.playerStatus ?? null,
    /*
      On a linked row the response carries the player record's values for these three, and the API
      validates the body (weight 50-400, lengths) before it knows the row is linked. Sending them
      back could fail an unrelated save, such as a role change, on a record's out-of-range weight,
      so a linked row sends null and the API keeps the record's values.

      School is one of them. The API applies these four to an unlinked row only, so a linked row
      sends null and the player record keeps its own values -- and what this row holds for school
      is only the record's value AS THIS BOARD LAST READ IT. A board loaded ten minutes ago, or a
      second tab, carries a string that may already be stale, so sending it back could only ever
      overwrite a correction made elsewhere, off the back of a pitching-role change that has
      nothing to do with schools.
    */
    height: linked ? null : player.height ?? null,
    weight: linked ? null : player.weight ?? null,
    school: linked ? null : player.school ?? null,
    travelTeam: linked ? null : player.travelTeam ?? null,
    committedSchool: player.committedSchool ?? null,
    velo: player.velo ?? null,
    armAngle: player.armAngle ?? null,
    pitchShapes: player.pitchShapes ?? [],
    pitchingRole: player.pitchingRole ?? null,
    ...changes,
  };
};
